import React, { useCallback, useState } from 'react';
import Cropper, { Area } from 'react-easy-crop';
import { FiX } from 'react-icons/fi';
import { MdCrop } from 'react-icons/md';

interface ImageCropModalProps {
  imageUrl: string;
  onCropComplete: (croppedImage: Blob) => void;
  onCancel: () => void;
  aspectRatio?: number;
}

/**
 * COMPONENTE: ImageCropModal
 *
 * Modal interactivo para recortar imágenes con vista previa en tiempo real
 *
 * CARACTERÍSTICAS:
 * - Recorte con relación de aspecto fija (default: 3:1)
 * - Zoom y pan interactivos
 * - Vista previa del resultado
 * - Controles intuitivos
 */
const ImageCropModal: React.FC<ImageCropModalProps> = ({
  imageUrl,
  onCropComplete,
  onCancel,
  aspectRatio = 2,
}) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const onCropChange = (location: { x: number; y: number }) => {
    setCrop(location);
  };

  const onZoomChange = (zoom: number) => {
    setZoom(zoom);
  };

  const onCropCompleteInternal = useCallback(
    (croppedArea: Area, croppedAreaPixels: Area) => {
      setCroppedAreaPixels(croppedAreaPixels);
    },
    [],
  );

  /**
   * Genera la imagen recortada a partir del área seleccionada
   */
  const createCroppedImage = async (): Promise<Blob> => {
    return new Promise((resolve, reject) => {
      const image = new Image();
      image.src = imageUrl;

      image.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        if (!ctx || !croppedAreaPixels) {
          reject(new Error('No se pudo crear el canvas'));
          return;
        }

        // Configurar dimensiones del canvas
        canvas.width = croppedAreaPixels.width;
        canvas.height = croppedAreaPixels.height;

        // Dibujar la imagen recortada
        ctx.drawImage(
          image,
          croppedAreaPixels.x,
          croppedAreaPixels.y,
          croppedAreaPixels.width,
          croppedAreaPixels.height,
          0,
          0,
          croppedAreaPixels.width,
          croppedAreaPixels.height,
        );

        // Convertir a blob
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Error al generar la imagen recortada'));
              return;
            }
            resolve(blob);
          },
          'image/jpeg',
          0.95,
        );
      };

      image.onerror = () => {
        reject(new Error('Error al cargar la imagen'));
      };
    });
  };

  const handleSave = async () => {
    if (!croppedAreaPixels) {
      return;
    }

    setIsProcessing(true);

    try {
      const croppedImage = await createCroppedImage();
      onCropComplete(croppedImage);
    } catch (error) {
      console.error('Error cropping image:', error);
      alert('Error al recortar la imagen');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-2">
            <MdCrop className="text-2xl text-blue-600" />
            <h2 className="text-xl font-bold text-gray-800">
              Ajustar imagen al banner
            </h2>
          </div>
          <button
            onClick={onCancel}
            className="text-gray-500 hover:text-gray-700 transition-colors"
            disabled={isProcessing}
          >
            <FiX className="text-2xl" />
          </button>
        </div>

        {/* Crop Area */}
        <div className="flex-1 relative bg-gray-900 min-h-[400px]">
          <Cropper
            image={imageUrl}
            crop={crop}
            zoom={zoom}
            aspect={aspectRatio}
            onCropChange={onCropChange}
            onZoomChange={onZoomChange}
            onCropComplete={onCropCompleteInternal}
          />
        </div>

        {/* Controls */}
        <div className="p-4 border-t bg-gray-50">
          {/* Zoom control */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Zoom
            </label>
            <input
              type="range"
              min={1}
              max={3}
              step={0.1}
              value={zoom}
              onChange={(e) => setZoom(parseFloat(e.target.value))}
              className="w-full"
            />
          </div>

          {/* Info */}
          <div className="bg-blue-50 border border-blue-200 rounded p-3 mb-4">
            <p className="text-sm text-blue-800">
              <strong>Instrucciones:</strong> Arrastra la imagen para
              posicionarla y usa el control de zoom para ajustar el tamaño. El
              área seleccionada será el banner final con relación {aspectRatio}
              :1
            </p>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3">
            <button
              onClick={onCancel}
              disabled={isProcessing}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              onClick={handleSave}
              disabled={isProcessing}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              <MdCrop />
              {isProcessing ? 'Procesando...' : 'Aplicar recorte'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImageCropModal;
